/**
 * The debug stream, read from the running binary. Story VC-014.
 *
 * `agent_runtime_lld.md` §30.7's endpoint and its three checks. This file
 * transports and decides nothing: `model.js` holds every rule about what the
 * frames mean.
 *
 * The framing is `voice-client-core`'s, injected the same way `text.js`
 * injects it and for the same reason — a `text/event-stream` arrives in
 * arbitrary chunks and an event is not a line, so `agent-sse` is the
 * workspace's one answer to that and a copy here would be the same bug with
 * no test able to see it.
 *
 * `EventSource` is unusable for the same reason as in the text path: the
 * credential is an `Authorization` header and `EventSource` cannot send one.
 *
 * # The debug credential is not the session credential
 *
 * §30.7: debug permission is a *separate* token, because a flag on the
 * session token "would let every holder of one read every inference request
 * in the tenant". The two are separate parameters here so nobody can pass one
 * for the other by accident.
 */

/**
 * Reads a session's debug stream until it ends or `signal` aborts.
 *
 * @param {{ base: string, debugToken: string }} config
 * @param {string} sessionId
 * @param {{ decoder: { push(chunk: Uint8Array): string[] },
 *           onFrame: (frame: any) => void,
 *           after?: number,
 *           types?: string[],
 *           includePayload?: boolean,
 *           signal: AbortSignal }} sink
 */
export async function readDebugEvents(
  { base, debugToken },
  sessionId,
  { decoder, onFrame, after, types, includePayload = true, signal },
) {
  const query = new URLSearchParams();
  if (after !== undefined) {
    query.set("after", String(after));
  }
  // §30.7 accepts a family or an exact type, repeated. Sent as repeats
  // rather than a comma list because that is what the handler parses.
  for (const type of types ?? []) {
    query.append("type", type);
  }
  if (!includePayload) {
    query.set("include_payload", "false");
  }
  const suffix = query.toString() ? `?${query}` : "";

  const response = await fetch(
    `${base}/v1/sessions/${sessionId}/debug/events${suffix}`,
    {
      headers: {
        accept: "text/event-stream",
        authorization: `Bearer ${debugToken}`,
      },
      signal,
    },
  );
  if (!response.ok || !response.body) {
    throw new Error(await describe(response));
  }

  const reader = response.body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }
    for (const payload of decoder.push(value)) {
      let frame;
      try {
        frame = JSON.parse(payload);
      } catch {
        // A frame that will not parse is dropped rather than fatal: the
        // stream is still framed correctly and the next frame is readable.
        // A framing failure is the decoder's and throws from `push`.
        continue;
      }
      onFrame(frame);
    }
  }
}

/**
 * What went wrong, with §30.7's own words where it has them.
 *
 * The three refusals mean different things and a reader who cannot tell them
 * apart will check the wrong thing: a 404 is "this deployment serves no debug
 * plane", not "wrong id".
 *
 * @param {Response} response
 */
/**
 * One stored payload, by the reference an event carried.
 *
 * §30.8's second endpoint, and the reason the first one stays small: a
 * payload over `max_payload_bytes` is kept by reference so that a stream of
 * them is not the reason a session is slow. The reader fetches what it
 * chose to look at.
 *
 * Resolvable only through the session whose record names it (§30.8,
 * `AGENTS.md` §I.16) — an id is not a capability, so the session is in the
 * path and the server checks it.
 *
 * @param {{ base: string, debugToken: string }} config
 * @param {string} sessionId
 * @param {string} blobId
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<unknown>} the payload, parsed
 */
export async function readDebugBlob(
  { base, debugToken },
  sessionId,
  blobId,
  { signal } = {},
) {
  const response = await fetch(
    `${base}/v1/sessions/${sessionId}/debug/blobs/${blobId}`,
    {
      headers: {
        accept: "application/json",
        authorization: `Bearer ${debugToken}`,
      },
      signal,
    },
  );
  if (!response.ok) {
    throw new Error(await describe(response));
  }
  return response.json();
}

async function describe(response) {
  let label = "";
  try {
    label = (await response.json()).error ?? "";
  } catch {
    // No body, or not JSON. The status still says which check refused.
  }
  const hint = {
    404: "no debug plane here, or not your session — set VOICE_DEBUG_TOKEN on the server",
    403: "this credential carries no debug permission — a group under an identity provider, ?debug_token= under a static deployment (§30.7)",
    401: "the debug token was refused",
    400: "the session id is not a uuid",
  }[response.status];
  const parts = [String(response.status), label, hint].filter(Boolean);
  return parts.join(" · ");
}
